<template>
  <ValidationObserver v-slot="{handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal :visible="addChargeModal.show"
                   :data="addChargeModal.settlement"
                   :title="$t('Add Vendor Charge')"
                   @show="onShow"
                   @hidden="onHidden"
                   @ok.prevent="handleSubmit(onSubmit)"
                   :ok-title="$t('Submit')"
                   :modal-loading="loading"
                   :no-close-on-backdrop="loading"
                   :no-close-on-esc="loading"
                   :hide-header-close="loading"
                   :disable-submit-btn="loading"
                   size="xl">
      <div class="row">
        <div class="col-md-12">
          <b-button class="p-0 mb-2" variant="link" @click="onManuallyAddChargeClick">
            <font-awesome-icon :icon="'plus'" style="font-size: .9em"></font-awesome-icon>
            {{ $t('Manually Add Miscellaneous Charge') }}
          </b-button>
        </div>
        <div class="col-md-12">
          <input-widget :model="addChargeModel" attribute="vendor_charge_uuid" type="select2" @select2Unselect="onUnselect"
              :items="addChargeModal.applicableVendorCharges" value-field="uuid" @select2Select="onSelect"
              display-field="${item.name}, level: ${item.level}, applies to: ${item.data.applies_to}"
              :disabled="!addChargeModal.settlement || !addChargeModal.settlement.uuid"/>
        </div>
      </div>
      <div class="row" v-if="charges.length">
        <div class="col-md-2"></div>
        <div class="col-md-3">{{ $t('Comment') }}</div>
        <div class="col-md-3">{{ $t('Amount') }}</div>
        <div class="col-md-2">{{ $t('VAT') }}</div>
        <div class="col-md-1">{{ $t('Taxable') }}</div>
        <div class="col-md-1"></div>
      </div>
      <div class="row" v-for="(charge, index) in charges" :key="index">
        <div class="col-md-2">
          <span v-if="charge.item_text">{{ charge.item_text }}</span>
          <input-widget v-else :label="false" :model="charge" attribute="description" :placeholder="$t('Name')"/>
        </div>
        <div class="col-md-3">
          <input-widget :label="false" :model="charge" attribute="comment" :placeholder="$t('Comment')"/>
        </div>
        <div class="col-md-3">
          <input-widget :label="false" :model="charge" attribute="amount" type="number" @input="updateVat(charge)"/>
        </div>
        <div class="col-md-2">
          <input-widget :label="false" :model="charge" attribute="vat" type="number" disabled/>
        </div>
        <div class="col-md-1">
          <input-widget :label="false" :model="charge" attribute="taxable" type="checkbox"
              :disabled="!!charge.item_text" @change="updateVat(charge)"/>
        </div>
        <div class="col-md-1">
          <b-tooltip :target="`delete-btn-${index}`" :title="$t('Delete')"/>
          <b-button size="sm" variant="danger" :id="`delete-btn-${index}`"
                    @click="onDeleteChargeClick(charge)">
            <font-awesome-icon icon="minus"/>
          </b-button>
        </div>
      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "@/core/components/artisio-modal/ArtisioModal";
  import InputWidget from "@/core/components/input-widget/InputWidget";
  import AddChargeModel from "./AddCharge.model.js";
  import SettlementService from "@/modules/settlements/SettlementService";
  import {createNamespacedHelpers} from "vuex";

  const {mapState, mapActions} = createNamespacedHelpers('settlements');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "AddChargeModal",
    components: {InputWidget, ArtisioModal},
    data() {
      return {
        addChargeModel: new AddChargeModel(),
        loading: false,
        charges: []
      }
    },
    computed: {
      ...mapState({
        addChargeModal: state => state.addChargeModal,
      }),
      ...globalMapState({
        defaultTaxRate: state => state.currentBranch.tax_rate
      })
    },
    methods: {
      ...mapActions(['hideAddChargeModal', 'addUnsavedCharges']),
      async onShow() {
        this.loading = true;
        let {success, body} = await SettlementService.calculateDropdownVendorCharges(this.addChargeModal.funcParams);
        this.loading = false;
        if (!success) {
          await this.$errorDialog(this.$i18n.t('Unable to fetch vendor charges for settled items'));
          return;
        }
        this.addChargeModal.applicableVendorCharges = body.map(i => new AddChargeModel(i));
      },
      onHidden() {
        this.addChargeModel = new AddChargeModel();
        this.charges = [];
        this.hideAddChargeModal();
      },
      async onSelect(data) {
        this.loading = true;
        this.charges = [...this.charges.filter(i => !i.vendor_charge_uuid)];
        let res = await SettlementService.calculateVendorCharge({
          financial_items_uuids: this.addChargeModal.funcParams.financial_items_uuids,
          existing_misc_items_signatures: this.addChargeModal.funcParams.existing_misc_items_signatures,
          unsaved_charges_signatures: this.addChargeModal.funcParams.unsaved_charges_signatures,
          charge_uuid: data.uuid,
          uuid: this.addChargeModal.settlement.uuid
        });
        this.loading = false;
        if (res.success) {
          for (let charge of res.body) {
            let add = true;
            for (let uvc of this.addChargeModal.unsavedCharges) {
              if (uvc.vendor_charge_uuid == data.uuid && (uvc.lot && uvc.lot.uuid == charge.lot.uuid || uvc.inventory && uvc.inventory.uuid == charge.inventory.uuid)) {
                add = false;
                break;
              }
            }
            if (!add) {
              continue;
            }
            let new_charge = {
              amount: charge['amount'],
              vat: charge['vat'],
              item_text: charge['item_text'],
              lot: charge.lot,
              inventory: charge.inventory,
              description: data.name,
              vendor_charge_uuid: data.uuid,
              receipt: charge.receipt,
              taxable: charge['is_taxable'],
              is_func: charge['is_func'],
              tax_rate: charge['tax_rate'],
              comment: charge['comment'],
              is_grouped: charge['is_grouped']
            };
            new_charge[`${data.level}_uuid`] = data[`${data.level}_uuid`];
            this.charges.push(new AddChargeModel(new_charge));
          }
          if (this.charges.length == 0) {
            this.$errorDialog(this.$i18n.t('This charge is already applied'));
          }
        } else {
          this.$errorDialog(res.body.non_field_errors[0]);
        }
      },
      onUnselect() {
        this.charges = [];
      },
      async onSubmit() {
        this.addChargeModal.settlementModal.unsavedCharges = [...this.addChargeModal.settlementModal.unsavedCharges, ...this.charges.filter(x => x.amount != 0)]
        this.addChargeModel = new AddChargeModel();
        this.charges = [];
        this.hideAddChargeModal();
      },
      onManuallyAddChargeClick() {
        this.charges.push(new AddChargeModel({amount: 0, vat: 0, tax_rate: this.defaultTaxRate}));
      },
      updateVat(charge) {
        this.$nextTick(() => {
          if (charge.taxable) {
            charge.vat = Math.round(charge.amount * Number(charge.tax_rate)) / 100;
          } else {
            charge.vat = 0;
          }
        });
      },
      onDeleteChargeClick(charge) {
        const indexInModel = this.charges.findIndex(c => c === charge);
        if (indexInModel !== -1) {
          this.charges.splice(indexInModel, 1);
        }
      }
    }
  }
</script>

<style scoped>
</style>
